import { useState } from "react";

import {
  QUADRANT_FILL,
  QUADRANT_LABEL,
  QUADRANT_LABEL_COLOR,
  TIER_COLOR,
  type PriorityQuadrant,
  type ScoredPair,
} from "./relationshipPriorityScoring";

interface Props {
  pairs: ScoredPair[];
  activeQuadrant: PriorityQuadrant | "all";
  onQuadrantClick: (quad: PriorityQuadrant) => void;
  /** Surfaces that pair's card below the matrix — quadrant-label click
   * already does this for a whole quadrant; bubble click previously only
   * toggled the selection ring's stroke with no card surfaced. */
  onBubbleClick?: (pairId: string) => void;
}

/**
 * mock `.pfm-matrix-wrap` (CSS ~672-677) + `renderPFMMatrix()` (JS
 * ~7109-7170). Geometry, colors and layout are ported verbatim — viewBox
 * 0 0 860 330, padL/R 56, padT 28, padB 44, bubble radius 20.
 */
const W = 860;
const H = 330;
const PAD_L = 56;
const PAD_R = 56;
const PAD_T = 28;
const PAD_B = 44;
const R = 20;

const PLOT_W = W - PAD_L - PAD_R;
const PLOT_H = H - PAD_T - PAD_B;
const HX = PAD_L + PLOT_W / 2;
const HY = PAD_T + PLOT_H / 2;

const QUADS: {
  id: PriorityQuadrant;
  x: number;
  y: number;
  w: number;
  h: number;
  lx: number;
  ly: number;
  anchor: "start" | "end";
}[] = [
  {
    id: "build",
    x: PAD_L,
    y: PAD_T,
    w: PLOT_W / 2,
    h: PLOT_H / 2,
    lx: PAD_L + 14,
    ly: PAD_T + 16,
    anchor: "start",
  },
  {
    id: "dofirst",
    x: HX,
    y: PAD_T,
    w: PLOT_W / 2,
    h: PLOT_H / 2,
    lx: W - PAD_R - 14,
    ly: PAD_T + 16,
    anchor: "end",
  },
  {
    id: "maintain",
    x: PAD_L,
    y: HY,
    w: PLOT_W / 2,
    h: PLOT_H / 2,
    lx: PAD_L + 14,
    ly: HY + 16,
    anchor: "start",
  },
  {
    id: "fasttrack",
    x: HX,
    y: HY,
    w: PLOT_W / 2,
    h: PLOT_H / 2,
    lx: W - PAD_R - 14,
    ly: HY + 16,
    anchor: "end",
  },
];

export default function RelationshipPriorityMatrix({
  pairs,
  activeQuadrant,
  onQuadrantClick,
  onBubbleClick,
}: Props) {
  const [activeBubble, setActiveBubble] = useState<string | null>(null);

  return (
    <div className="w-full overflow-hidden rounded-[10px] border border-gray-200 bg-white [aspect-ratio:860/330] max-h-[430px]">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="block h-full w-full"
        role="img"
        aria-label="Urgency by importance priority matrix"
      >
        {QUADS.map(q => {
          const isActive = activeQuadrant === "all" || activeQuadrant === q.id;
          const fill = isActive
            ? QUADRANT_FILL[q.id]
            : "rgba(200,200,200,0.04)";
          const labelColor = isActive ? QUADRANT_LABEL_COLOR[q.id] : "#d1d5db";
          return (
            <g key={q.id}>
              <rect x={q.x} y={q.y} width={q.w} height={q.h} fill={fill} />
              <text
                x={q.lx}
                y={q.ly}
                fontSize={9.5}
                fontWeight={800}
                fill={labelColor}
                textAnchor={q.anchor}
                letterSpacing="0.08em"
                style={{ cursor: "pointer" }}
                onClick={() => onQuadrantClick(q.id)}
              >
                {QUADRANT_LABEL[q.id]}
              </text>
            </g>
          );
        })}

        <line
          x1={PAD_L}
          y1={HY}
          x2={W - PAD_R}
          y2={HY}
          stroke="#e5e7eb"
          strokeWidth={1.5}
          strokeDasharray="4,3"
        />
        <line
          x1={HX}
          y1={PAD_T}
          x2={HX}
          y2={H - PAD_B}
          stroke="#e5e7eb"
          strokeWidth={1.5}
          strokeDasharray="4,3"
        />
        <rect
          x={PAD_L}
          y={PAD_T}
          width={PLOT_W}
          height={PLOT_H}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={1.5}
          rx={2}
        />

        <text
          x={PAD_L + PLOT_W / 2}
          y={H - 8}
          fontSize={10}
          fontWeight={700}
          fill="#9ca3af"
          textAnchor="middle"
          letterSpacing="0.05em"
        >
          URGENCY →
        </text>
        <text
          x={PAD_L - 10}
          y={PAD_T + PLOT_H / 2}
          fontSize={10}
          fontWeight={700}
          fill="#9ca3af"
          textAnchor="middle"
          letterSpacing="0.05em"
          transform={`rotate(-90,${PAD_L - 10},${PAD_T + PLOT_H / 2})`}
        >
          IMPORTANCE ↑
        </text>

        <text x={PAD_L + 4} y={H - PAD_B + 12} fontSize={9} fill="#c4c8d0">
          Low
        </text>
        <text
          x={W - PAD_R - 4}
          y={H - PAD_B + 12}
          fontSize={9}
          fill="#c4c8d0"
          textAnchor="end"
        >
          High
        </text>
        <text
          x={PAD_L - 4}
          y={H - PAD_B - 2}
          fontSize={9}
          fill="#c4c8d0"
          textAnchor="end"
        >
          Low
        </text>
        <text
          x={PAD_L - 4}
          y={PAD_T + 10}
          fontSize={9}
          fill="#c4c8d0"
          textAnchor="end"
        >
          High
        </text>

        {pairs.map(({ pair, urgency, importance, tier, quadrant, score }) => {
          if (tier === "unassessed" || !quadrant) return null;
          const cx = PAD_L + urgency * PLOT_W;
          const cy = PAD_T + (1 - importance) * PLOT_H;
          const color = TIER_COLOR[tier];
          const isSelected = activeBubble === pair.pair_id;
          const inQuad =
            activeQuadrant === "all" || activeQuadrant === quadrant;
          const initials = pair.contact_name
            .split(" ")
            .slice(0, 2)
            .map(n => n[0])
            .join("");
          return (
            <g
              key={pair.pair_id}
              opacity={inQuad ? 1 : 0.22}
              style={{ cursor: "pointer" }}
              onClick={() => {
                setActiveBubble(prev =>
                  prev === pair.pair_id ? null : pair.pair_id
                );
                onBubbleClick?.(pair.pair_id);
              }}
            >
              <circle
                cx={cx}
                cy={cy}
                r={R}
                fill={color}
                stroke={isSelected ? "#0f1623" : color}
                strokeWidth={isSelected ? 2.5 : 1.5}
              />
              <text
                x={cx}
                y={cy - 3}
                fontSize={9}
                fontWeight={800}
                fill="white"
                textAnchor="middle"
              >
                {initials}
              </text>
              <text
                x={cx}
                y={cy + 8}
                fontSize={8.5}
                fill="rgba(255,255,255,0.92)"
                textAnchor="middle"
              >
                {score}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
